export type Position =
  'top' |
  'right' |
  'bottom' |
  'left' |
  'top-left' |
  'top-center' |
  'top-right' |
  'bottom-left' |
  'bottom-center' |
  'bottom-right' |
  'left-top' |
  'left-center' |
  'left-bottom' |
  'left-bottomArrow' |
  'right-top' |
  'right-center' |
  'right-bottom' |
  'right-bottomArrow'
  ;

export type SimplePosition =
  'center' |
  'right' |
  'left';

export const getElementClientRect = (element: HTMLElement, setViewportOffset = true): Partial<DOMRect> => {
  const elBcr = element.getBoundingClientRect();

  const viewportOffset = {
    top: (setViewportOffset ? (window.pageYOffset - document.documentElement.clientTop) : 0),
    left: (setViewportOffset ? (window.pageXOffset - document.documentElement.clientLeft) : 0),
  };
  return {
    height: elBcr.height || element.offsetHeight,
    width: elBcr.width || element.offsetWidth,
    top: elBcr.top + viewportOffset.top,
    bottom: elBcr.bottom + viewportOffset.top,
    left: elBcr.left + viewportOffset.left,
    right: elBcr.right + viewportOffset.left,
  };
};

export const isInViewport = (elementClientRect: Partial<DOMRect>): boolean => {
  const html = document.documentElement;
  const windowHeight = window.innerHeight || html.clientHeight;
  const windowWidth = window.innerWidth || html.clientWidth;
  return elementClientRect.left >= 0 && elementClientRect.top >= 0 && elementClientRect.right <= windowWidth
    && elementClientRect.bottom <= windowHeight;
};

export const scrollTo = (offset: number, callback: () => void) => {
  const fixedOffset = offset.toFixed();
  const onScroll = () => {
    if (window.pageYOffset.toFixed() === fixedOffset) {
      window.removeEventListener('scroll', onScroll);
      callback();
    }
  };

  window.addEventListener('scroll', onScroll);
  onScroll();
  window.scrollTo({
    top: offset,
    behavior: 'smooth',
  });
};

export const setDefaultPositionPriority = (initPosition: Position): Array<Position> => {
  const positionPriority: Array<Position> = [];
  const [primaryPosition = 'top', secondaryPosition = 'center'] = (initPosition || '').toString().split('-');
  positionPriority.push(initPosition);
  switch (primaryPosition) {
    case 'top':
      positionPriority.push(initPosition.replace('top-', 'bottom-') as Position);
      positionPriority.push(initPosition.replace('top-', 'left-') as Position);
      positionPriority.push(initPosition.replace('top-', 'right-') as Position);
      break;
    case 'bottom':
      positionPriority.push(initPosition.replace('bottom-', 'top-') as Position);
      positionPriority.push(initPosition.replace('bottom-', 'left-') as Position);
      positionPriority.push(initPosition.replace('bottom-', 'right-') as Position);
      break;
    case 'left':
      positionPriority.push(initPosition.replace('left-', 'right-') as Position);
      positionPriority.push(initPosition.replace('left-', 'top-') as Position);
      positionPriority.push(initPosition.replace('left-', 'bottom-') as Position);
      break;
    case 'right':
      positionPriority.push(initPosition.replace('right-', 'left-') as Position);
      positionPriority.push(initPosition.replace('right-', 'top-') as Position);
      positionPriority.push(initPosition.replace('right-', 'bottom-') as Position);
      break;
  }
  return positionPriority;
};

export const placeElement = (
  hostElement: HTMLElement,
  targetElement: HTMLElement,
  position: Position | Array<Position>,
  overlayElement: HTMLElement = undefined,
  scrollToHost = false,
  blockScrolling = false,
  tooltipOffsetLeft = 0
): Position => {
  targetElement.style.opacity = '0';
  targetElement.style.transition = 'opacity ease-in .1s';
  const positionPriorityArray: Array<Position> = (position instanceof Array) ? position : setDefaultPositionPriority(position);
  const targetComputedStyle = window.getComputedStyle(targetElement);
  let hostClientRect = getElementClientRect(hostElement, !scrollToHost);
  const targetClientRect = getElementClientRect(targetElement, scrollToHost);
  const targetMarginBottom = parseFloat(targetComputedStyle.marginBottom);
  const targetMarginRight = parseFloat(targetComputedStyle.marginRight);
  let displayedPosition: Position;

  // Check if the targetElement is inside the viewport
  if (!scrollToHost || (scrollToHost && isInViewport(hostClientRect))) {
    blockScrolling && (document.body.style.overflow = 'hidden');
    let tooltipIsInViewport = false;
    let i = 0;
    if (positionPriorityArray && positionPriorityArray.length > 0) {
      do {
        tooltipIsInViewport = placeElementFunction(targetElement, positionPriorityArray[i], hostClientRect,
          targetClientRect, targetMarginBottom, targetMarginRight, tooltipOffsetLeft);
        displayedPosition = positionPriorityArray[i];
        i = i + 1;
      } while (!tooltipIsInViewport && (i < 4));
    }
    !!overlayElement && placeOverlay(hostElement, overlayElement);
    targetElement.style.opacity = '1';
  } else {
    scrollTo(Math.max(hostClientRect.top - (window.innerHeight / 2), 0), () => {
      blockScrolling && (document.body.style.overflow = 'hidden');
      hostClientRect = getElementClientRect(hostElement, false);
      if (positionPriorityArray && positionPriorityArray.length > 0) {
        placeElementFunction(targetElement, positionPriorityArray[0], hostClientRect, targetClientRect,
          targetMarginBottom, targetMarginRight, tooltipOffsetLeft);
        displayedPosition = positionPriorityArray[0];
        !!overlayElement && placeOverlay(hostElement, overlayElement);
        targetElement.style.opacity = '1';
      }
    });
  }

  return displayedPosition;
}

export const placeElementFunction = (
  targetElement: HTMLElement,
  position: Position,
  hostClientRect: Partial<DOMRect>,
  targetClientRect: Partial<DOMRect>,
  targetMarginBottom: number,
  targetMarginRight: number,
  tooltipOffsetLeft: number
) => {

  const [primaryPosition = 'top', secondaryPosition = 'center'] = (position || '').toString().split('-');

  targetElement.style.position = 'absolute';

  let topPosition = 0;
  let leftPosition = 0;

  switch (primaryPosition) {
    case 'top':
      topPosition = hostClientRect.top - (targetClientRect.height + targetMarginBottom);
      leftPosition = hostClientRect.left;
      break;
    case 'bottom':
      topPosition = hostClientRect.top + (hostClientRect.height);
      leftPosition = hostClientRect.left;
      break;
    case 'left':
      topPosition = hostClientRect.top;
      leftPosition = hostClientRect.left - (targetClientRect.width + targetMarginRight);
      break;
    case 'right':
      topPosition = hostClientRect.top;
      leftPosition = hostClientRect.left + (hostClientRect.width);
      break;
  }

  switch (secondaryPosition) {
    case 'top':
      topPosition = hostClientRect.top;
      break;
    case 'bottom':
      topPosition = hostClientRect.top + hostClientRect.height - targetClientRect.height;
      break;
    case 'bottomArrow':
      topPosition = hostClientRect.top + (hostClientRect.height / 2) - (targetClientRect.height - 27);
      break;
    case 'left':
      leftPosition = hostClientRect.left;
      break;
    case 'right':
      leftPosition = hostClientRect.left + hostClientRect.width - targetClientRect.width;
      break;
    case 'center':
      if (primaryPosition === 'top' || primaryPosition === 'bottom') {
        leftPosition = (hostClientRect.left + hostClientRect.width / 2 - targetClientRect.width / 2);
      } else {
        topPosition = (hostClientRect.top + hostClientRect.height / 2 - targetClientRect.height / 2);
      }
      break;
  }

  targetElement.style.top = `${topPosition}px`;
  targetElement.style.left = `${leftPosition + tooltipOffsetLeft}px`;

  // Check if the targetElement is inside the viewport
  targetClientRect = getElementClientRect(targetElement, false);
  return isInViewport(targetClientRect);
};

export const placeOverlay = (
  hostElement: HTMLElement,
  targetElement: HTMLElement,
  offset = 10,
  forceRounded = true,
) => {
  const hostClientRect = getElementClientRect(hostElement, false);

  targetElement.style.position = 'absolute';

  targetElement.style.top = `${ ((forceRounded) ?
    hostClientRect.top - ((Math.max(hostClientRect.height, hostClientRect.width) - hostClientRect.height) / 2)
    : hostClientRect.top) - (offset / 2) }px`;
  targetElement.style.left = `${ ((forceRounded) ?
    hostClientRect.left - ((Math.max(hostClientRect.height, hostClientRect.width) - hostClientRect.width) / 2)
    : hostClientRect.left) - (offset / 2) }px`;
  targetElement.style.height = `${ ((forceRounded) ? Math.max(hostClientRect.height, hostClientRect.width)
    : hostClientRect.height) + offset }px`;
  targetElement.style.width = `${ ((forceRounded) ? Math.max(hostClientRect.height, hostClientRect.width)
    : hostClientRect.width) + offset }px`;
};

